import { useTranslation } from "react-i18next";
import {
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  notification,
  Row,
  Select,
  Tag,
  Tooltip,
} from "antd";
import { addDoc, collection, doc, updateDoc } from "firebase/firestore";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MARKETING_CODES,
  PRODUCT_COMPANY_GROUPS,
  PRODUCT_STOCK_COUNTRIES,
} from "../../../../../constants/ProductConstant";
import { DELIVERY_DAYS, RETAIL_DELIVERY_LABEL } from "../../../../../constants/OrderConstant";
import i18n from "../../../../../translations/i18n";
import TextArea from "antd/es/input/TextArea";
import { CHEFS_FARM, COMPANY_COUNTRY_CODE, GROWY } from "../../../../../constants/CompanyConstants";
import { CURRENCY, DEFAULT_DELIVERY_COST, FREE_DELIVERY_FROM_PRICE } from "../../../../../constants/DeliveryConstants";
import { USER_STATUS_ACTIVE } from "../../../../../constants/UserConstant";
import {
  DEFAULT_CREDIT_LIMIT,
  PAYMENT_METHOD_INVOICE,
  PAYMENT_METHODS,
} from "../../../../../constants/PaymentConstants";
import dayjs from "dayjs";
import { FIRESTORE_CUSTOMERS_TABLE, FIRESTORE_USERS_TABLE } from "../../../../../constants/FirestoreConstant";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { createExactAccount } from "../../../../../functions/api/exact";
import { formatPhoneNumber } from "../../../../../functions/formatters";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { useCountries } from "../../../../../data/hooks/firestore/useCountries";
import { InfoCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import urls from "../../../../../urls";
import {
  findDeliveryZoneByPostalCode,
  getDeliveryZoneStatus,
  sanitizePostalCode,
} from "../../../../../functions/deliveryZones";

export default function AddCompanyModal({
  visible,
  onClose,
  exactCustomers,
  initialValues = null,
  prefillValues = null,
  onSuccess = null,
}) {
  const { t } = useTranslation();
  const { email } = useAuth();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isExactCustomer, setIsExactCustomer] = useState(false);
  const [isDifferentInvoiceAddress, setIsDifferentInvoiceAddress] = useState(false);
  const [companyNameSameAsRestaurant, setCompanyNameSameAsRestaurant] = useState(true);

  // Normalize timeslot to JSON with consistent key order to prevent display mismatches
  const stringifyTimeSlot = (slot) =>
    JSON.stringify({ start: slot.start, end: slot.end, deliverySurcharge: Number(slot.deliverySurcharge || 0) });

  const isEditMode = !!initialValues;
  const companyValue = Form.useWatch("company", form);
  const countryValue = Form.useWatch("country", form);
  const deliveryLabelValue = Form.useWatch("deliveryLabel", form);
  const zipCodeValue = Form.useWatch("zipCode", form);
  const [deliveryZone, setDeliveryZone] = useState(null);
  const { getCountryPricing, getCountryDelivery, getCountryDeliveryLabels } = useCountries();
  const defaultCreditLimit = getCountryPricing(countryValue)?.defaultCreditLimit ?? DEFAULT_CREDIT_LIMIT;
  const defaultDeliverySettings = getCountryDelivery(countryValue || COMPANY_COUNTRY_CODE);
  const isCountryInitialized = useRef(false);
  const isDeliveryLabelInitialized = useRef(false);

  useEffect(() => {
    if (!isCountryInitialized.current) {
      isCountryInitialized.current = true;
      return;
    }
    form.setFieldValue("creditLimit", defaultCreditLimit);
    form.setFieldValue("deliveryCost", defaultDeliverySettings?.defaultDeliveryCost || DEFAULT_DELIVERY_COST);
    form.setFieldValue(
      "deliveryCostThreshold",
      defaultDeliverySettings?.deliveryCostThreshold || FREE_DELIVERY_FROM_PRICE,
    );

    // Only auto-set delivery label in ADD mode or if customer doesn't have one
    if (!isEditMode || !initialValues?.deliveryLabel) {
      const defaultLabel = defaultDeliverySettings?.defaultLabel || null;
      form.setFieldValue("deliveryLabel", defaultLabel);

      // Auto-set delivery days based on default label
      if (defaultLabel && defaultDeliverySettings?.deliveryLabels) {
        const labelConfig = defaultDeliverySettings.deliveryLabels.find((dl) => dl.deliveryLabel === defaultLabel);
        if (labelConfig?.deliveryDays) {
          form.setFieldValue("customerDeliveryDays", labelConfig.deliveryDays);
        }
        // Auto-set first time slot if available
        if (labelConfig?.deliveryTimeSlots?.length > 0) {
          form.setFieldValue("deliveryTimeSlot", stringifyTimeSlot(labelConfig.deliveryTimeSlots[0]));
        }
      }
    }
  }, [countryValue, defaultCreditLimit, defaultDeliverySettings, isEditMode, initialValues]);

  const isGrowyCustomer = useMemo(() => {
    return companyValue === GROWY;
  }, [companyValue]);

  // Pre-fill form in add mode (e.g. from lead conversion)
  useEffect(() => {
    if (visible && !initialValues && prefillValues) {
      form.setFieldsValue({
        company: CHEFS_FARM,
        customerName: prefillValues.customerName || "",
        contactFirstName: prefillValues.contact?.firstName || "",
        contactLastName: prefillValues.contact?.lastName || "",
        customerEmail: prefillValues.customerEmail || "",
        customerPhone: prefillValues.customerPhone || "",
        street: prefillValues.street || "",
        houseNumber: prefillValues.houseNumber || "",
        houseNumberAddition: prefillValues.houseNumberAddition || "",
        zipCode: prefillValues.zipCode || "",
        city: prefillValues.city || "",
        country: prefillValues.country || PRODUCT_STOCK_COUNTRIES[0].code,
        deliveryLabel: prefillValues.deliveryLabel || null,
        deliveryTimeSlot: prefillValues.deliveryTimeSlot ? stringifyTimeSlot(prefillValues.deliveryTimeSlot) : null,
      });
    }
  }, [visible, initialValues, prefillValues, form]);

  // Initialize form when initialValues change
  useEffect(() => {
    if (visible && initialValues) {
      // Check if different invoice address exists
      const hasDifferentInvoiceAddress =
        initialValues.customerAddress !== initialValues.customerBillingAddress &&
        !!initialValues.customerBillingAddress;
      setIsDifferentInvoiceAddress(hasDifferentInvoiceAddress);

      // Check if companyName differs from customerName
      const hasCompanyName = initialValues.companyName && initialValues.companyName !== initialValues.customerName;
      setCompanyNameSameAsRestaurant(!hasCompanyName);

      const formValues = {
        company: CHEFS_FARM, // Always CF for pending
        customerName: initialValues.customerName,
        companyName: initialValues.companyName || "",
        contactFirstName: initialValues.contact?.firstName || "",
        contactLastName: initialValues.contact?.lastName || "",
        customerEmail: initialValues.customerEmail,
        customerPhone: initialValues.customerPhone,
        kvkNumber: initialValues.kvkNumber || 0,
        taxNumber: initialValues.taxNumber || null,
        customerGln: initialValues.customerGln || null,
        customerDeliveryGln: initialValues.customerDeliveryGln || null,
        street: initialValues.street,
        houseNumber: initialValues.houseNumber,
        houseNumberAddition: initialValues.houseNumberAddition,
        zipCode: initialValues.zipCode,
        city: initialValues.city,
        country: initialValues.country || PRODUCT_STOCK_COUNTRIES[0].code,
        deliveryCost:
          initialValues.deliveryCost || defaultDeliverySettings?.defaultDeliveryCost || DEFAULT_DELIVERY_COST,
        deliveryCostThreshold:
          initialValues.deliveryCostThreshold ||
          defaultDeliverySettings?.deliveryCostThreshold ||
          FREE_DELIVERY_FROM_PRICE,
        customerDeliveryDays: initialValues.customerDeliveryDays
          ? initialValues.customerDeliveryDays.split(",").map(Number)
          : DELIVERY_DAYS.split(",").map(Number),
        deliveryLabel: initialValues.deliveryLabel,
        deliveryTimeSlot: initialValues.deliveryTimeSlot ? stringifyTimeSlot(initialValues.deliveryTimeSlot) : null,
        deliveryNotes: initialValues.deliveryNotes,
        paymentMethod: initialValues.paymentMethod || PAYMENT_METHOD_INVOICE,
        creditLimit:
          initialValues.creditLimit ||
          getCountryPricing(initialValues.country)?.defaultCreditLimit ||
          DEFAULT_CREDIT_LIMIT,
        paymentCardNumber: initialValues.paymentCardNumber || "",
        paymentCardHolder: initialValues.paymentCardHolder || "",
        customerDiscount: initialValues.customerSnelstartDiscount || 0,
        invoiceEmail: initialValues.invoiceEmail || null,
      };

      // Add invoice address fields if different
      if (hasDifferentInvoiceAddress) {
        formValues.invoiceStreet = initialValues.invoiceStreet;
        formValues.invoiceHouseNumber = initialValues.invoiceHouseNumber;
        formValues.invoiceHouseNumberAddition = initialValues.invoiceHouseNumberAddition;
        formValues.invoiceZipCode = initialValues.invoiceZipCode;
        formValues.invoiceCity = initialValues.invoiceCity;
        formValues.invoiceCountry = initialValues.invoiceCountry;
      }

      form.setFieldsValue(formValues);
    }
  }, [visible, initialValues, form]);

  // Auto-detect delivery zone for French postal codes
  useEffect(() => {
    if (countryValue === "FR" && zipCodeValue) {
      const sanitized = sanitizePostalCode(zipCodeValue);
      const countryConfig = getCountryDelivery(countryValue);
      const zone = findDeliveryZoneByPostalCode(sanitized, countryConfig);
      setDeliveryZone(zone);
    } else {
      setDeliveryZone(null);
    }
  }, [zipCodeValue, countryValue, getCountryDelivery]);

  useEffect(() => {
    if (isGrowyCustomer) {
      form.setFieldsValue({
        deliveryLabel: RETAIL_DELIVERY_LABEL,
      });
    } else if (!isEditMode) {
      form.setFieldsValue({
        deliveryLabel: null,
      });
    }
  }, [isGrowyCustomer, isEditMode, form]);

  // Auto-populate delivery days and time slot when delivery label changes
  useEffect(() => {
    if (!isDeliveryLabelInitialized.current) {
      isDeliveryLabelInitialized.current = true;
      return;
    }

    if (!deliveryLabelValue || !defaultDeliverySettings?.deliveryLabels) return;

    const labelConfig = defaultDeliverySettings.deliveryLabels.find((dl) => dl.deliveryLabel === deliveryLabelValue);

    if (labelConfig) {
      // Only auto-set in ADD mode or if customer doesn't have existing values
      if (!isEditMode || !initialValues?.customerDeliveryDays) {
        if (labelConfig.deliveryDays) {
          form.setFieldValue("customerDeliveryDays", labelConfig.deliveryDays);
        }
      }

      if (!isEditMode || !initialValues?.deliveryTimeSlot) {
        if (labelConfig.deliveryTimeSlots?.length > 0) {
          form.setFieldValue("deliveryTimeSlot", stringifyTimeSlot(labelConfig.deliveryTimeSlots[0]));
        }
      }
    }
  }, [deliveryLabelValue, defaultDeliverySettings, isEditMode, initialValues, form]);

  const handleSubmit = () => {
    form.validateFields().then(async (values) => {
      setIsLoading(true);

      try {
        const address =
          values.street.trim() +
          " " +
          values.houseNumber.trim() +
          (values.houseNumberAddition ? " " + values.houseNumberAddition.trim() : "") +
          ", " +
          values.city.trim() +
          ", " +
          values.zipCode.trim();

        // Parse delivery time slot safely
        let deliveryTimeSlotObject = null;
        if (values.deliveryTimeSlot) {
          try {
            deliveryTimeSlotObject = JSON.parse(values.deliveryTimeSlot);
          } catch (e) {
            console.error("Error parsing time slot:", e);
            deliveryTimeSlotObject = null;
          }
        }

        const obj = {
          exactId: values.exactId || null,
          exactAccountCode: null,
          country: values.country,
          customerEmail: values.customerEmail,
          customerPhone: values.customerPhone ? formatPhoneNumber(values.customerPhone) : null,
          customerDeliveryDays: Array.isArray(values.customerDeliveryDays)
            ? values.customerDeliveryDays.join(",")
            : values.customerDeliveryDays || DELIVERY_DAYS,
          deliveryTimeSlot: deliveryTimeSlotObject,
          customerAddress: address,
          customerBillingAddress: address,
          customerUsers: initialValues?.customerUsers || [],
          customerOrders: initialValues?.customerOrders || [],
          customerShoppingCarts: initialValues?.customerShoppingCarts || [],
          customerRegion: COMPANY_COUNTRY_CODE,
          deliveryCost: values.deliveryCost || defaultDeliverySettings?.defaultDeliveryCost || DEFAULT_DELIVERY_COST,
          deliveryCostThreshold:
            values.deliveryCostThreshold || defaultDeliverySettings?.deliveryCostThreshold || FREE_DELIVERY_FROM_PRICE,
          status: USER_STATUS_ACTIVE,
          paymentMethod: values.paymentMethod || PAYMENT_METHOD_INVOICE,
          invoiceEmail: values.invoiceEmail || null,
          addressIsVerified: true,
          city: values.city.trim(),
          houseNumber: values.houseNumber.trim(),
          houseNumberAddition: values.houseNumberAddition?.trim() || null,
          street: values.street.trim(),
          zipCode: values.zipCode.trim(),
          customerSnelstartDiscount: values.customerDiscount || 0,
          deliveryNotes: values.deliveryNotes || null,
          deliveryLabel: values.deliveryLabel || null,
          kvkNumber: Number(values.kvkNumber) || 0,
          taxNumber: values.taxNumber || null,
          customerGln: values.customerGln || null,
          customerDeliveryGln: values.customerDeliveryGln || null,
          paymentCardHolder: values.paymentCardHolder || "",
          paymentCardNumber: values.paymentCardNumber || "",
          creditLimit:
            values.creditLimit || getCountryPricing(values.country)?.defaultCreditLimit || DEFAULT_CREDIT_LIMIT,
          updatedAt: dayjs().unix(),
          updatedBy: email,
          company: values.company,
        };

        // Add contact info
        obj.contact = {
          firstName: values.contactFirstName.trim(),
          lastName: values.contactLastName.trim(),
        };
        obj.customerName = values.customerName.trim();

        // Handle companyName based on checkbox state
        if (companyNameSameAsRestaurant) {
          // If checkbox is checked, don't save companyName (or explicitly set to null to remove it)
          obj.companyName = null;
        } else {
          // If checkbox is unchecked, save the companyName
          obj.companyName = values.companyName?.trim() || null;
        }

        if (values.internalNotes) {
          obj.internalNotes = { text: values.internalNotes, createdAt: dayjs().unix(), createdBy: email };
        }

        obj.marketingCode = values.marketingCode;

        // Handle invoice address
        if (isDifferentInvoiceAddress) {
          obj.customerBillingAddress =
            values.invoiceStreet.trim() +
            " " +
            values.invoiceHouseNumber.trim() +
            (values.invoiceHouseNumberAddition ? " " + values.invoiceHouseNumberAddition.trim() : "") +
            ", " +
            values.invoiceCity.trim() +
            ", " +
            values.invoiceZipCode.trim();
          obj.invoiceStreet = values.invoiceStreet.trim();
          obj.invoiceHouseNumber = values.invoiceHouseNumber.trim();
          obj.invoiceHouseNumberAddition = values.invoiceHouseNumberAddition?.trim() || null;
          obj.invoiceZipCode = values.invoiceZipCode.trim();
          obj.invoiceCity = values.invoiceCity.trim();
          obj.invoiceCountry = values.invoiceCountry;
        } else {
          obj.customerBillingAddress = address;
          obj.invoiceStreet = values.street.trim();
          obj.invoiceHouseNumber = values.houseNumber.trim();
          obj.invoiceHouseNumberAddition = values.houseNumberAddition?.trim() || null;
          obj.invoiceZipCode = values.zipCode.trim();
          obj.invoiceCity = values.city.trim();
          obj.invoiceCountry = values.country;
        }

        const token = await getFirebaseIDToken();

        if (isEditMode) {
          // Edit mode: Update existing customer and create in Exact (PENDING CUSTOMERS ONLY)
          const objectWithId = {
            ...obj,
            id: initialValues.id,
            customerId: initialValues.id,
            contact: {
              userFirstName: values.contactFirstName,
              userLastName: values.contactLastName,
            },
          };

          // First update with base data
          await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, initialValues.id), obj);

          // Activate linked user(s)
          if (initialValues.customerUsers && initialValues.customerUsers.length > 0) {
            await Promise.all(
              initialValues.customerUsers.map((userId) =>
                updateDoc(doc(firestore, FIRESTORE_USERS_TABLE, userId), {
                  userStatus: USER_STATUS_ACTIVE,
                  updatedAt: dayjs().unix(),
                  updatedBy: email,
                }),
              ),
            );
          }

          // Send acceptance email to customer
          try {
            await axios.post(urls.sendCustomerRequestAcceptedMail, {
              email: values.customerEmail,
              contactPerson: `${values.contactFirstName} ${values.contactLastName}`,
              companyName: values.customerName,
              region: values.country,
              userId: initialValues.customerUsers?.[0] || null,
            });
          } catch (emailError) {
            console.error("Failed to send acceptance email:", emailError);
            // Don't fail the whole operation if email fails
          }

          // Then create in Exact (API will update Firestore with exactId and exactAccountCode)
          try {
            await createExactAccount(objectWithId, token);
          } catch (err) {
            console.error(err);
            const errorMessage =
              typeof err === "string"
                ? err
                : err.response?.data?.message || err.message || t("notifications.save_to_exact_failed");

            notification.error({
              message: t("error"),
              description: (
                <div>
                  <p>
                    <strong>{t("notifications.customer_updated_but_exact_failed")}</strong>
                  </p>
                  <p>
                    {t("notifications.exact_error_details")}: {errorMessage}
                  </p>
                  <p>{t("notifications.fix_and_retry_via_red_cross")}</p>
                </div>
              ),
              duration: 0,
            });

            closeModal();
            return; // Stop execution here
          }

          message.success(t("request_accepted"));
          closeModal();
        } else {
          // Add mode: Create new customer
          obj.createdAt = dayjs().unix();

          if (isExactCustomer && values.exactId && !!exactCustomers) {
            const exactCustomer = exactCustomers.find((item) => item.ID === values.exactId);
            if (!!exactCustomer) {
              obj.exactAccountCode = exactCustomer.AccountCode.trim();
              obj.customerName = exactCustomer.AccountName;
              obj.contact = { firstName: exactCustomer.ContactFirstName, lastName: exactCustomer.ContactLastName };
            }
          }

          const docRef = await addDoc(collection(firestore, FIRESTORE_CUSTOMERS_TABLE), obj);
          const customerId = docRef.id;
          await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, docRef.id), {
            customerId,
            updatedAt: dayjs().unix(),
            updatedBy: email,
          });

          if (!isExactCustomer) {
            const object = {
              ...obj,
              customerId,
              contact: { userFirstName: values.contactFirstName, userLastName: values.contactLastName },
            };
            try {
              await createExactAccount(object, token);
            } catch (err) {
              console.error(err);
              const errorMessage =
                typeof err === "string"
                  ? err
                  : err.response?.data?.message || err.message || t("notifications.save_to_exact_failed");

              notification.error({
                message: t("error"),
                description: (
                  <div>
                    <p>
                      <strong>{t("notifications.customer_created_but_exact_failed")}</strong>
                    </p>
                    <p>
                      {t("notifications.exact_error_details")}: {errorMessage}
                    </p>
                    <p>{t("notifications.fix_and_retry_via_red_cross")}</p>
                  </div>
                ),
                duration: 0,
              });

              onSuccess?.();
              closeModal();
              return; // Stop execution here
            }
          }

          message.success(t("notifications.customer_added"));
          onSuccess?.();
          closeModal();
        }
      } catch (err) {
        console.error(err);
        message.error(
          t("notifications.save_to_exact", {
            error: !!err.response?.data ? err.response.data.message : err.message,
          }),
          15,
        );
      } finally {
        setIsLoading(false);
      }
    });
  };

  const closeModal = () => {
    setIsExactCustomer(false);
    setIsDifferentInvoiceAddress(false);
    setCompanyNameSameAsRestaurant(true);
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={isEditMode ? t("edit_company_data", { company: initialValues?.customerName || "" }) : t("add_customer")}
      open={visible}
      onOk={handleSubmit}
      onCancel={closeModal}
      okText={isEditMode ? t("save_and_accept") : t("save")}
      cancelText={t("close")}
      okButtonProps={{ loading: isLoading, htmlType: "submit" }}
      centered
      width={800}
      zIndex={1002}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
        initialValues={{
          customerDiscount: 0,
          country: PRODUCT_STOCK_COUNTRIES[0].code,
          customerDeliveryDays: DELIVERY_DAYS.split(",").map(Number),
          deliveryCost: defaultDeliverySettings?.defaultDeliveryCost || DEFAULT_DELIVERY_COST,
          deliveryCostThreshold: defaultDeliverySettings?.deliveryCostThreshold || FREE_DELIVERY_FROM_PRICE,
          paymentMethod: PAYMENT_METHOD_INVOICE,
          creditLimit: defaultCreditLimit,
          kvkNumber: 0,
          company: isEditMode ? CHEFS_FARM : undefined,
        }}
      >
        {/* Basic Company Info */}
        <Row gutter={16}>
          <Col xs={12}>
            <Form.Item name="country" label={t("country")}>
              <Select className="mr-2">
                {PRODUCT_STOCK_COUNTRIES.map((country) => (
                  <Select.Option key={country.code} value={country.code}>
                    {i18n.t(country.label)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={12} />
          <Col xs={12}>
            <Form.Item
              name="company"
              label={t("company_group")}
              rules={[{ required: true, message: t("form.select_company") }]}
            >
              <Select placeholder={t("choose_a_company")} disabled={isEditMode}>
                {PRODUCT_COMPANY_GROUPS.map((company) => (
                  <Select.Option key={company.value} value={company.value}>
                    {company.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              name="marketingCode"
              label={t("customer.type")}
              rules={[{ required: true, message: t("form.select_customer_type") }]}
            >
              <Select placeholder={t("choose_a_customer_type")}>
                {MARKETING_CODES.map((code) => (
                  <Select.Option key={code.value} value={code.value}>
                    {code.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Exact customer checkbox - only in add mode */}
        {!isEditMode && (
          <Row>
            <Col xs={24} style={{ display: "flex", justifyContent: "end" }}>
              <Checkbox checked={isExactCustomer} onChange={(e) => setIsExactCustomer(e.target.checked)}>
                {t("exact_connected")}
              </Checkbox>
            </Col>
          </Row>
        )}

        {/* Exact customer select - only in add mode with checkbox checked */}
        {!isEditMode && isExactCustomer ? (
          <Row>
            <Col xs={24}>
              <Form.Item
                name={"exactId"}
                label={t("exact_customer")}
                rules={[{ required: true, message: t("form.select_customer") }]}
              >
                <Select
                  style={{ width: "100%" }}
                  placeholder={t("exact_customer_id")}
                  showSearch
                  optionFilterProp="children"
                >
                  {exactCustomers?.map((item) => (
                    <Select.Option key={item.ID} value={item.ID}>
                      {`${item.AccountName} (${item.AccountCode.trim()})`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        ) : (
          <>
            <Row>
              <Col xs={24}>
                <Form.Item
                  name={"customerName"}
                  label={
                    <span>
                      {companyValue === CHEFS_FARM ? t("restaurant_name") : t("customer_name")}{" "}
                      <Tooltip title={t("customer_name_tooltip")}>
                        <InfoCircleOutlined style={{ color: "rgba(0,0,0,.45)" }} />
                      </Tooltip>
                    </span>
                  }
                  rules={[
                    {
                      required: true,
                      message:
                        companyValue === CHEFS_FARM ? t("form.enter_restaurant_name") : t("form.enter_customer_name"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col xs={24} style={{ marginBottom: 12 }}>
                <Checkbox
                  checked={companyNameSameAsRestaurant}
                  onChange={(e) => setCompanyNameSameAsRestaurant(e.target.checked)}
                >
                  {companyValue === CHEFS_FARM
                    ? t("company_name_same_as_restaurant")
                    : t("company_name_same_as_customer")}
                </Checkbox>
              </Col>
            </Row>
            {!companyNameSameAsRestaurant && (
              <Row>
                <Col xs={24}>
                  <Form.Item
                    name={"companyName"}
                    label={t("company_name")}
                    rules={[{ required: true, message: t("form.enter_company_name") }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Row gutter={16}>
              <Col xs={24} lg={12}>
                <Form.Item
                  name={"contactFirstName"}
                  label={t("customer.contact_first_name")}
                  rules={[{ required: true, message: t("form.enter_contact_first_name") }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                  name={"contactLastName"}
                  label={t("customer.contact_last_name")}
                  rules={[{ required: true, message: t("form.enter_contact_last_name") }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item
              name={"customerEmail"}
              label={t("email")}
              rules={[
                {
                  required: true,
                  message: t("form.enter_email"),
                },
                {
                  type: "email",
                  message: t("form.enter_valid_email"),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              name={"customerPhone"}
              label={t("phone")}
              rules={[
                {
                  pattern: /^[+]?(?:[0-9][\s\-]*){10,15}$/,
                  message: t("form.enter_valid_phone"),
                },
                {
                  type: "tel",
                },
              ]}
              tooltip={{ title: t("enter_phone_tooltip"), icon: <InfoCircleOutlined /> }}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col xs={24}>
            <Form.Item name={"internalNotes"} label={t("internal_notes")}>
              <TextArea placeholder={t("internal_customer_notes_placeholder")} />
            </Form.Item>
          </Col>
        </Row>

        {/* Company Registration */}
        <Divider>{t("company_registration")}</Divider>
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item name={"kvkNumber"} label={t("kvk_number")}>
              <InputNumber placeholder={t("kvk_number")} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name={"taxNumber"} label={t("vat_number")}>
              <Input placeholder={t("vat_number")} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              name={"customerGln"}
              label={t("gln_number")}
              rules={[
                {
                  pattern: /^[0-9]{13}$/,
                  message: t("form.enter_valid_gln"),
                },
              ]}
            >
              <Input placeholder={t("gln_number")} maxLength={13} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              name={"customerDeliveryGln"}
              label={t("gln_delivery_number")}
              tooltip={{ title: t("form.gln_delivery_number_tooltip") }}
              rules={[
                {
                  pattern: /^[0-9]{13}$/,
                  message: t("form.enter_valid_gln"),
                },
              ]}
            >
              <Input placeholder={t("gln_delivery_number")} maxLength={13} />
            </Form.Item>
          </Col>
        </Row>

        {/* Delivery Address */}
        <Divider>{t("delivery_address")}</Divider>
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              name={"street"}
              label={t("street")}
              rules={[
                {
                  required: true,
                  message: t("form.enter_street"),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={12}>
            <Form.Item
              name={"zipCode"}
              label={
                <span>
                  {t("zip_code")}
                  {countryValue === "FR" && deliveryZone && (
                    <Tag color={getDeliveryZoneStatus(deliveryZone).color} style={{ marginLeft: 8 }}>
                      {deliveryZone.deliveryTime}
                    </Tag>
                  )}
                  {countryValue === "FR" && zipCodeValue && !deliveryZone && (
                    <Tag color="orange" style={{ marginLeft: 8 }}>
                      {t("delivery_zone_unknown")}
                    </Tag>
                  )}
                </span>
              }
              rules={[
                {
                  required: true,
                  message: t("form.enter_zip_code"),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} lg={6}>
            <Form.Item
              name={"houseNumber"}
              label={t("house_number")}
              rules={[
                {
                  required: true,
                  message: t("form.enter_house_number"),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} lg={6}>
            <Form.Item name={"houseNumberAddition"} label={t("addition")}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={12}>
            <Form.Item
              name={"city"}
              label={t("city")}
              rules={[
                {
                  required: true,
                  message: t("form.enter_city"),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {/* Invoice Address */}
        <Row gutter={16}>
          <Col xs={24} style={{ marginBottom: 12 }}>
            <Checkbox
              checked={isDifferentInvoiceAddress}
              onChange={(e) => setIsDifferentInvoiceAddress(e.target.checked)}
            >
              {t("form.different_invoice_address")}
            </Checkbox>
          </Col>

          {isDifferentInvoiceAddress && (
            <>
              <Col xs={24}>
                <h4 style={{ marginTop: 16, marginBottom: 16 }}>{t("invoice_address")}</h4>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name={"invoiceStreet"}
                  label={t("street")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_street"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={12}>
                <Form.Item
                  name={"invoiceZipCode"}
                  label={t("zip_code")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_zip_code"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={12} lg={6}>
                <Form.Item
                  name={"invoiceHouseNumber"}
                  label={t("house_number")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_house_number"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={12} lg={6}>
                <Form.Item name={"invoiceHouseNumberAddition"} label={t("addition")}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  name={"invoiceCity"}
                  label={t("city")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_city"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={12}>
                <Form.Item
                  name="invoiceCountry"
                  label={t("country")}
                  rules={[
                    {
                      required: true,
                      message: t("form.enter_country"),
                    },
                  ]}
                >
                  <Select className="mr-2">
                    {PRODUCT_STOCK_COUNTRIES.map((country) => (
                      <Select.Option key={country.code} value={country.code}>
                        {i18n.t(country.label)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </>
          )}
        </Row>

        {/* Delivery Settings */}
        <Divider>{t("delivery_settings")}</Divider>
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item name={"deliveryCost"} label={t("delivery_cost")}>
              <InputNumber
                placeholder={t("delivery_cost")}
                style={{ width: "100%" }}
                min={0}
                precision={2}
                step={0.05}
                formatter={(value) => `${CURRENCY} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => {
                  const parsedValue = value.replace(new RegExp(`${CURRENCY}\\s?|(,*)`, "g"), "");
                  return parsedValue === "" ? 0 : Number(parsedValue);
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name={"deliveryCostThreshold"} label={t("delivery_cost_threshold")}>
              <InputNumber
                placeholder={t("delivery_cost_threshold")}
                style={{ width: "100%" }}
                precision={2}
                step={0.5}
                min={0}
                controls={false}
                formatter={(value) => `${CURRENCY} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => {
                  const parsedValue = value.replace(new RegExp(`${CURRENCY}\\s?|(,*)`, "g"), "");
                  return parsedValue === "" ? 0 : Number(parsedValue);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item
              name="deliveryLabel"
              label={t("delivery_label")}
              rules={[
                {
                  required: true,
                  message: t("form.select_delivery_label"),
                },
              ]}
            >
              <Select style={{ width: "100%" }} placeholder={t("delivery_label_placeholder")}>
                {getCountryDeliveryLabels(countryValue).map((deliveryLabel) => (
                  <Select.Option key={deliveryLabel.code} value={deliveryLabel.code}>
                    {deliveryLabel.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              name="deliveryTimeSlot"
              label={t("delivery_time_slot")}
              rules={[
                {
                  required: true,
                  message: t("form.select_delivery_time_slot"),
                },
              ]}
            >
              <Select
                style={{ width: "100%" }}
                placeholder={t("select_delivery_time_slot")}
                disabled={!deliveryLabelValue}
              >
                {deliveryLabelValue &&
                  defaultDeliverySettings?.deliveryLabels
                    ?.find((dl) => dl.deliveryLabel === deliveryLabelValue)
                    ?.deliveryTimeSlots?.map((slot, index) => (
                      <Select.Option key={index} value={stringifyTimeSlot(slot)}>
                        {slot.start} - {slot.end}
                        {slot.deliverySurcharge > 0 && ` (+€${slot.deliverySurcharge})`}
                      </Select.Option>
                    ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item name={"customerDeliveryDays"} label={t("delivery_days")}>
              <Select mode="multiple" placeholder={t("delivery_days")} allowClear style={{ width: "100%" }}>
                <Select.Option value={1}>{t("week_days.monday")}</Select.Option>
                <Select.Option value={2}>{t("week_days.tuesday")}</Select.Option>
                <Select.Option value={3}>{t("week_days.wednesday")}</Select.Option>
                <Select.Option value={4}>{t("week_days.thursday")}</Select.Option>
                <Select.Option value={5}>{t("week_days.friday")}</Select.Option>
                <Select.Option value={6}>{t("week_days.saturday")}</Select.Option>
                <Select.Option value={0}>{t("week_days.sunday")}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <Form.Item name={"deliveryNotes"} label={t("delivery_notes")}>
              <TextArea placeholder={t("delivery_notes_placeholder")} />
            </Form.Item>
          </Col>
        </Row>

        {/* Payment Details */}
        <Divider>{t("payment_details")}</Divider>
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item
              name="paymentMethod"
              label={t("payment_method")}
              rules={[
                {
                  required: true,
                  message: t("form.select_payment_method"),
                },
              ]}
            >
              <Select style={{ width: "100%" }} placeholder={t("select_payment_method")}>
                {PAYMENT_METHODS.map((method) => (
                  <Select.Option key={method} value={method}>
                    {t(method.toLowerCase().replace(" ", "_"))}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name={"creditLimit"} label={t("credit_limit")}>
              <InputNumber
                placeholder={t("credit_limit")}
                style={{ width: "100%" }}
                step={50}
                min={0}
                controls={false}
                formatter={(value) => `${CURRENCY} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => {
                  const parsedValue = value.replace(new RegExp(`${CURRENCY}\\s?|(,*)`, "g"), "");
                  return parsedValue === "" ? 0 : Number(parsedValue);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item name="paymentCardNumber" label={t("payment_card_number")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="paymentCardHolder" label={t("payment_card_holder")}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={12}>
            <Form.Item name={"customerDiscount"} label={t("customer_discount")}>
              <InputNumber
                placeholder={t("customer_discount")}
                style={{ width: "100%" }}
                min={0}
                max={100}
                formatter={(value) => `${value || 0}%`}
                parser={(value) => {
                  if (value === null || value === undefined) return 0;
                  const parsed = value.replace("%", "");
                  return parsed === "" ? 0 : Number(parsed);
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              name={"invoiceEmail"}
              label={t("invoice_email")}
              rules={[
                {
                  type: "email",
                  message: t("form.enter_valid_email"),
                },
              ]}
            >
              <Input placeholder={t("invoice_email")} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
